/**
 * Hook for accessing user role-based permissions.
 *
 * Provides computed properties for role checks and permission flags
 * based on the Dossier role hierarchy.
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { DossierRole, UserPermissions } from '../types';

/**
 * Role hierarchy (higher = more permissions):
 * - system_admin: Full access
 * - group_admin: Manage group + personal watchlist
 * - user: Personal watchlist only
 * - viewer: Read-only, receives pushed content
 */
const ROLE_HIERARCHY: Record<DossierRole, number> = {
  system_admin: 4,
  group_admin: 3,
  user: 2,
  viewer: 1,
};

/**
 * Map Keycloak roles to Dossier roles.
 * Keycloak may use different role names than our internal hierarchy.
 */
const KEYCLOAK_ROLE_MAP: Record<string, DossierRole> = {
  // Direct mappings
  system_admin: 'system_admin',
  group_admin: 'group_admin',
  user: 'user',
  viewer: 'viewer',
  // Keycloak legacy mappings
  admin: 'group_admin', // Keycloak 'admin' maps to Dossier 'group_admin'
};

/**
 * Permissions matrix by role.
 */
const ROLE_PERMISSIONS: Record<DossierRole, UserPermissions> = {
  system_admin: {
    canManageAllUsers: true,
    canManageAllGroups: true,
    canManageAllCompanies: true,
    canTriggerKbBuilds: true,
    canManagePersonalWatchlist: true,
    canRemoveAnyCompany: true,
    canManageGroupMembers: true,
    canManageGroupPinnedCompanies: true,
    canViewReports: true,
    canReceivePushedUpdates: true,
    canUseChat: true,
  },
  group_admin: {
    canManageAllUsers: false,
    canManageAllGroups: false,
    canManageAllCompanies: false,
    canTriggerKbBuilds: false,
    canManagePersonalWatchlist: true,
    canRemoveAnyCompany: false,
    canManageGroupMembers: true,
    canManageGroupPinnedCompanies: true,
    canViewReports: true,
    canReceivePushedUpdates: true,
    canUseChat: true,
  },
  user: {
    canManageAllUsers: false,
    canManageAllGroups: false,
    canManageAllCompanies: false,
    canTriggerKbBuilds: false,
    canManagePersonalWatchlist: true,
    canRemoveAnyCompany: false,
    canManageGroupMembers: false,
    canManageGroupPinnedCompanies: false,
    canViewReports: true,
    canReceivePushedUpdates: true,
    canUseChat: true,
  },
  viewer: {
    canManageAllUsers: false,
    canManageAllGroups: false,
    canManageAllCompanies: false,
    canTriggerKbBuilds: false,
    canManagePersonalWatchlist: false,
    canRemoveAnyCompany: false,
    canManageGroupMembers: false,
    canManageGroupPinnedCompanies: false,
    canViewReports: true,
    canReceivePushedUpdates: true,
    canUseChat: false,
  },
};

export interface UseUserRoleResult {
  /** User's primary role (highest in hierarchy) */
  primaryRole: DossierRole | null;
  /** All roles the user has */
  roles: DossierRole[];
  /** Role-based boolean flags */
  isSystemAdmin: boolean;
  isGroupAdmin: boolean;
  isUser: boolean;
  isViewer: boolean;
  /** Computed permissions */
  permissions: UserPermissions;
  /** Check if user has at least a specific role level */
  hasRoleOrHigher: (role: DossierRole) => boolean;
  /** Check if user can manage watchlist */
  canManageWatchlist: boolean;
  /** Check if user can remove a company from watchlist */
  canRemoveCompany: (isPinned: boolean) => boolean;
}

/**
 * Get permissions for the highest role the user has.
 */
function getEffectivePermissions(roles: DossierRole[]): UserPermissions {
  // Find highest role
  let highestRole: DossierRole = 'viewer';
  let highestLevel = 0;

  for (const role of roles) {
    const level = ROLE_HIERARCHY[role];
    if (level > highestLevel) {
      highestLevel = level;
      highestRole = role;
    }
  }

  return ROLE_PERMISSIONS[highestRole];
}

/**
 * Hook to access user role information and permissions.
 *
 * @example
 * const { isSystemAdmin, canManageWatchlist, permissions } = useUserRole();
 *
 * if (canManageWatchlist) {
 *   // Show add/remove buttons
 * }
 */
export function useUserRole(): UseUserRoleResult {
  const { user } = useAuth();

  return useMemo(() => {
    // Extract Dossier-specific roles from user's roles, applying Keycloak mapping
    const dossierRoles: DossierRole[] = [];
    const seenRoles = new Set<DossierRole>();

    if (user?.roles) {
      for (const role of user.roles) {
        // Map Keycloak role to Dossier role
        const mappedRole = KEYCLOAK_ROLE_MAP[role];
        if (mappedRole && !seenRoles.has(mappedRole)) {
          seenRoles.add(mappedRole);
          dossierRoles.push(mappedRole);
        }
      }
    }

    // Determine primary role (highest in hierarchy)
    let primaryRole: DossierRole | null = null;
    let highestLevel = 0;

    for (const role of dossierRoles) {
      const level = ROLE_HIERARCHY[role];
      if (level > highestLevel) {
        highestLevel = level;
        primaryRole = role;
      }
    }

    // Computed role flags based on mapped Dossier roles
    const isSystemAdmin = dossierRoles.includes('system_admin');
    const isGroupAdmin = dossierRoles.includes('group_admin') || isSystemAdmin;
    const isUser = dossierRoles.includes('user') || isGroupAdmin;
    const isViewer = dossierRoles.includes('viewer') || isUser;

    // Get effective permissions
    const permissions = dossierRoles.length > 0
      ? getEffectivePermissions(dossierRoles)
      : ROLE_PERMISSIONS.viewer;

    // Can manage watchlist if not a viewer
    const canManageWatchlist = permissions.canManagePersonalWatchlist;

    // Check if user has at least a certain role level
    const hasRoleOrHigher = (role: DossierRole): boolean => {
      const requiredLevel = ROLE_HIERARCHY[role];
      return highestLevel >= requiredLevel;
    };

    // Check if user can remove a company
    const canRemoveCompany = (isPinned: boolean): boolean => {
      if (isViewer && !isUser) {
        return false; // Viewers can't remove anything
      }
      if (isPinned) {
        // Only group admins and above can remove pinned companies
        return isGroupAdmin;
      }
      // Regular users can remove personal companies
      return canManageWatchlist;
    };

    return {
      primaryRole,
      roles: dossierRoles,
      isSystemAdmin,
      isGroupAdmin,
      isUser,
      isViewer,
      permissions,
      hasRoleOrHigher,
      canManageWatchlist,
      canRemoveCompany,
    };
  }, [user]);
}
